import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import StatTile from './StatTile';
import styles from './Dashboard.module.css';
import dashboardService, { DashboardSummary } from '../../services/dashboardService';
import eventService, { Event } from '../../services/eventService';
import orderService from '../../services/orderService';
import {
  OrdersIcon,
  DollarIcon,
  VendorsIcon,
  ClockIcon,
  PlusIcon,
  EventsIcon,
  AnalyticsIcon,
  ArrowRightIcon,
  CheckIcon,
  CalendarIcon,
} from '../UI/Icons';

const PIE_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444'];

interface PromotionActivity {
  id: string;
  type: string;
  title: string;
  message: string;
  time: string;
  status: string;
  ctr: number;
}

interface DashboardPromotionStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalClicks: number;
  averageCTR: string;
}

interface DashboardProps {
  promotionStats: DashboardPromotionStats;
  onAddVendor: () => void;
}

interface ChartPoint {
  date: string;
  revenue: number;
  orders: number;
}

function getWaitTimeTone(waitTimeStr: string): 'success' | 'warning' | 'danger' | 'neutral' {
  const match = waitTimeStr?.match(/(\d+)/);
  if (!match) return 'neutral';
  const minutes = parseInt(match[1], 10);
  if (minutes < 15) return 'success';
  if (minutes <= 25) return 'warning';
  return 'danger';
}

function formatRevenueForTile(revenue: string | undefined): string {
  if (!revenue) return '$0';
  const numeric = parseFloat(revenue.replace('$', '').replace(/,/g, ''));
  if (Number.isNaN(numeric)) return revenue;
  if (numeric >= 1000) return `$${(numeric / 1000).toFixed(1)}k`;
  return `$${Math.round(numeric)}`;
}

function CardSkeleton({ height = 160 }: { height?: number }) {
  return (
    <div className={styles.cardSkeleton} aria-hidden="true">
      <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
      <div className={`${styles.skeletonBlock} ${styles.skeletonChart}`} style={{ height }} />
    </div>
  );
}

const Dashboard: React.FC<DashboardProps> = ({ promotionStats, onAddVendor }) => {
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState<Event[]>([]);
  const [promotionActivities, setPromotionActivities] = useState<PromotionActivity[]>([]);
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [apiData, setApiData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('oni_token');
    if (!token) {
      setLoading(false);
      return;
    }

    const loadDashboardData = async () => {
      try {
        setLoading(true);

        let startDate: Date | undefined;
        let endDate: Date | undefined;
        const now = new Date();

        if (dateRange === 'today') {
          startDate = new Date(now);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(now);
          endDate.setHours(23, 59, 59, 999);
        } else if (dateRange === 'yesterday') {
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = new Date(yesterday);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(yesterday);
          endDate.setHours(23, 59, 59, 999);
        } else if (dateRange === 'week') {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(now);
          endDate.setHours(23, 59, 59, 999);
        } else if (dateRange === 'month') {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 30);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(now);
          endDate.setHours(23, 59, 59, 999);
        } else if (dateRange === 'custom') {
          if (!customStartDate || !customEndDate) {
            setLoading(false);
            return;
          }
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }

        const data = await dashboardService.getSummary(
          undefined,
          startDate?.toISOString(),
          endDate?.toISOString()
        );
        setApiData(data);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    const loadPromotionsData = async () => {
      try {
        const promotionsList = await eventService.getAll({ type: 'promotion' });
        setPromotions(promotionsList);

        const activities: PromotionActivity[] = promotionsList
          .filter((p) => p.status === 'active' || p.status === 'scheduled')
          .map((promo) => ({
            id: promo._id || promo.id || '',
            type: promo.status === 'active' ? 'campaign_live' : 'campaign_scheduled',
            title: promo.title || promo.name,
            message: promo.status === 'active'
              ? `Campaign is live with ${promo.analytics?.clicks || 0} clicks`
              : `Scheduled to start ${new Date(promo.startDate).toLocaleDateString()}`,
            time: promo.status === 'active' ? 'Live now' : 'Upcoming',
            status: promo.status,
            ctr: promo.analytics?.ctr || 0,
          }))
          .sort((a, b) => {
            if (a.status === 'active' && b.status !== 'active') return -1;
            if (b.status === 'active' && a.status !== 'active') return 1;
            return 0;
          })
          .slice(0, 5);

        setPromotionActivities(activities);
      } catch (error) {
        console.error('Error loading promotions:', error);
        setPromotions([]);
        setPromotionActivities([]);
      }
    };

    loadPromotionsData();

    const handlePromotionUpdate = () => loadPromotionsData();
    window.addEventListener('promotionsUpdated', handlePromotionUpdate);
    return () => window.removeEventListener('promotionsUpdated', handlePromotionUpdate);
  }, []);

  useEffect(() => {
    const loadChartData = async () => {
      try {
        const orders = await orderService.getAll({ limit: 500 });
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const dailyMap = new Map<string, { revenue: number; orders: number }>();

        for (let i = 6; i >= 0; i--) {
          const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
          const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          dailyMap.set(key, { revenue: 0, orders: 0 });
        }

        orders
          .filter(o => new Date(o.createdAt || o.placedAt || '').getTime() >= sevenDaysAgo)
          .forEach(o => {
            const key = new Date(o.createdAt || o.placedAt || '').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const existing = dailyMap.get(key) || { revenue: 0, orders: 0 };
            dailyMap.set(key, {
              revenue: existing.revenue + (o.totalAmount || 0),
              orders: existing.orders + 1,
            });
          });

        setChartData(Array.from(dailyMap.entries()).map(([date, v]) => ({ date, ...v })));
      } catch {
        // Non-fatal
      }
    };
    loadChartData();
  }, []);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active': return styles.statusActive;
      case 'scheduled': return styles.statusScheduled;
      case 'paused': return styles.statusPaused;
      default: return styles.statusDraft;
    }
  };

  const orderSpark = chartData.map(d => d.orders);
  const revenueSpark = chartData.map(d => d.revenue);
  const totalOrders = apiData?.totalOrders || 0;
  const delivered = apiData?.orderBreakdown?.delivered || 0;
  const deliveryRate = totalOrders ? Math.round((delivered / totalOrders) * 100) : 0;

  const pieEntries = apiData?.orderBreakdown ? [
    { name: 'Pending', value: apiData.orderBreakdown.pending || 0, color: PIE_COLORS[0] },
    { name: 'Confirmed', value: apiData.orderBreakdown.confirmed || 0, color: PIE_COLORS[1] },
    { name: 'Preparing', value: apiData.orderBreakdown.preparing || 0, color: PIE_COLORS[2] },
    { name: 'Out for Delivery', value: apiData.orderBreakdown.outForDelivery || 0, color: PIE_COLORS[3] },
    { name: 'Delivered', value: apiData.orderBreakdown.delivered || 0, color: PIE_COLORS[4] },
  ] : [];
  const pieWithData = pieEntries.filter(e => e.value > 0);

  const topVendors = apiData?.topVendors ?? [];

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>Overview of your operations</p>
        </div>

        <div className={styles.dateRangePicker}>
          {(['today', 'yesterday', 'week', 'month', 'custom'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`${styles.dateButton} ${dateRange === range ? styles.dateButtonActive : ''}`}
              aria-pressed={dateRange === range}
            >
              {range === 'today' && 'Today'}
              {range === 'yesterday' && 'Yesterday'}
              {range === 'week' && 'Last 7 Days'}
              {range === 'month' && 'Last 30 Days'}
              {range === 'custom' && 'Custom Range'}
            </button>
          ))}

          {dateRange === 'custom' && (
            <div className={styles.customDateInputs}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className={styles.dateInput}
              />
              <span className={styles.dateSeparator}>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                min={customStartDate}
                className={styles.dateInput}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.actionBar}>
        <button type="button" className={styles.actionPill} onClick={onAddVendor}>
          <PlusIcon size={16} />
          Add Vendor
        </button>
        <button type="button" className={styles.actionPill} onClick={() => navigate('/admin/events?new=1')}>
          <PlusIcon size={16} />
          New Promotion
        </button>
        <button type="button" className={styles.actionPill} onClick={() => navigate('/admin/orders')}>
          <OrdersIcon size={16} />
          Orders
          <ArrowRightIcon size={14} />
        </button>
        <button type="button" className={styles.actionPill} onClick={() => navigate('/admin/analytics')}>
          <AnalyticsIcon size={16} />
          Analytics
          <ArrowRightIcon size={14} />
        </button>
      </div>

      <div className={styles.statRow}>
        <StatTile
          icon={<OrdersIcon size={16} />}
          label="Total Orders"
          value={apiData?.totalOrders ?? 0}
          trend={apiData?.trends?.orders?.value ? { value: apiData.trends.orders.value, isUp: !!apiData.trends.orders.isUp } : null}
          spark={orderSpark}
          sparkColor="var(--brand-primary)"
          loading={loading}
        />
        <StatTile
          icon={<DollarIcon size={16} />}
          label="Total Revenue"
          value={formatRevenueForTile(apiData?.totalRevenue)}
          trend={apiData?.trends?.revenue?.value ? { value: apiData.trends.revenue.value, isUp: !!apiData.trends.revenue.isUp } : null}
          spark={revenueSpark}
          sparkColor="var(--color-success)"
          loading={loading}
        />
        <StatTile
          icon={<VendorsIcon size={16} />}
          label="Active Vendors"
          value={apiData?.totalVendors ?? 0}
          subtitle={apiData?.activeEvents != null ? `${apiData.activeEvents} events live` : undefined}
          loading={loading}
        />
        <StatTile
          icon={<ClockIcon size={16} />}
          label="Avg Wait Time"
          value={apiData?.avgWaitTime || 'No data'}
          trend={apiData?.trends?.waitTime?.value ? { value: apiData.trends.waitTime.value, isUp: !!apiData.trends.waitTime.isUp } : null}
          trendTone={getWaitTimeTone(apiData?.avgWaitTime || '')}
          loading={loading}
        />
      </div>

      <div className={styles.mainGrid}>
        <div className={`${styles.gridCard} ${styles.spanWide}`}>
          <h3 className={styles.cardTitle}>Revenue Trends</h3>
          {loading ? (
            <CardSkeleton />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-success)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="var(--color-success)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className={styles.chartGrid} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} className={styles.chartAxis} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} className={styles.chartAxis} />
                <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`, 'Revenue']} contentStyle={{ background: 'var(--color-surface-primary)', border: '1px solid var(--color-border-primary)', borderRadius: 8 }} />
                <Area type="monotone" dataKey="revenue" stroke="var(--color-success)" fill="url(#revGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`${styles.gridCard} ${styles.spanNarrow}`}>
          <h3 className={styles.cardTitle}>Order Status Mix</h3>
          {loading ? (
            <CardSkeleton height={180} />
          ) : apiData?.orderBreakdown ? (
            <div className={styles.donutWrap}>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={pieWithData}
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieWithData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => v} />
                </PieChart>
              </ResponsiveContainer>
              <div className={styles.donutCenter}>
                <span className={styles.donutRate}>{deliveryRate}%</span>
                <span className={styles.donutLabel}>delivered</span>
              </div>
              {pieWithData.length > 0 && (
                <div className={styles.pieLegend}>
                  {pieWithData.map((entry, i) => (
                    <div key={i} className={styles.pieLegendItem}>
                      <span className={styles.pieLegendDot} style={{ background: entry.color }} />
                      {entry.name} <strong>{entry.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>No data for period</div>
          )}
        </div>

        <div className={`${styles.gridCard} ${styles.spanWide}`}>
          <h3 className={styles.cardTitle}>Order Volume</h3>
          {loading ? (
            <CardSkeleton />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className={styles.chartGrid} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} className={styles.chartAxis} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} className={styles.chartAxis} />
                <Tooltip formatter={(v: number) => [v, 'Orders']} contentStyle={{ background: 'var(--color-surface-primary)', border: '1px solid var(--color-border-primary)', borderRadius: 8 }} />
                <Line type="monotone" dataKey="orders" stroke="var(--brand-primary)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={`${styles.gridCard} ${styles.spanNarrow}`}>
          <h3 className={styles.cardTitle}>Order Summary</h3>
          <div className={styles.orderSummary}>
            {loading ? (
              <CardSkeleton height={180} />
            ) : apiData?.orderBreakdown ? (
              (['pending', 'confirmed', 'preparing', 'outForDelivery', 'delivered'] as const).map((key) => {
                const label = key === 'outForDelivery' ? 'Out for Delivery' : key.charAt(0).toUpperCase() + key.slice(1);
                const count = apiData.orderBreakdown[key] || 0;
                const pct = totalOrders ? (count / totalOrders) * 100 : 0;
                return (
                  <div key={key} className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>{label}</span>
                    <div className={styles.summaryProgress}>
                      <div className={styles.summaryProgressBar}>
                        <div className={styles.summaryProgressFill} style={{ width: `${pct}%` }} />
                      </div>
                      <span className={styles.summaryValue}>{count}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.emptyState}>No order data available</div>
            )}
          </div>
        </div>

        <div className={`${styles.gridCard} ${styles.spanHalf}`}>
          <h3 className={styles.cardTitle}>Top Performing Vendors</h3>
          <div className={styles.topList}>
            {loading ? (
              <>
                {[1, 2, 3].map(i => (
                  <div key={i} className={styles.listItem}>
                    <div className={`${styles.skeletonBlock} ${styles.skeletonRank}`} />
                    <div className={`${styles.skeletonBlock} ${styles.skeletonListLine}`} />
                  </div>
                ))}
              </>
            ) : topVendors.length > 0 ? (
              topVendors.map((vendor, index) => (
                <div key={vendor.name} className={styles.listItem}>
                  <div className={`${styles.rank} ${index === 0 ? styles.rankFirst : styles.rankOther}`}>
                    {index + 1}
                  </div>
                  <span className={styles.itemName}>{vendor.name}</span>
                  <span className={styles.itemValue}>
                    {vendor.orderCount} orders · ${Number(vendor.revenue).toFixed(0)}
                  </span>
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>No data available yet</div>
            )}
          </div>
        </div>

        <div className={`${styles.gridCard} ${styles.spanHalf}`}>
          <h3 className={styles.cardTitle}>
            <span className={styles.cardTitleLeft}>
              <EventsIcon size={16} />
              Events & Promotions
            </span>
            {promotions.filter(p => p.status === 'active').length > 0 && (
              <span className={styles.activeIndicator}>
                {promotions.filter(p => p.status === 'active').length} Active
              </span>
            )}
          </h3>
          <div className={styles.promotionActivities}>
            {promotionActivities.length > 0 ? (
              promotionActivities.map(activity => (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={styles.activityIcon}>
                    {activity.status === 'active' ? (
                      <CheckIcon size={16} className={styles.iconActive} />
                    ) : (
                      <CalendarIcon size={16} className={styles.iconScheduled} />
                    )}
                  </div>
                  <div className={styles.activityContent}>
                    <div className={styles.activityHeader}>
                      <span className={styles.activityTitle}>{activity.title}</span>
                      <span className={`${styles.activityStatus} ${getStatusBadgeClass(activity.status)}`}>
                        {activity.status}
                      </span>
                    </div>
                    <p className={styles.activityMessage}>{activity.message}</p>
                    <div className={styles.activityMeta}>
                      <span className={styles.activityTime}>{activity.time}</span>
                      {activity.ctr > 0 && (
                        <span className={styles.activityCtr}>CTR: {activity.ctr}%</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyActivities}>
                <EventsIcon size={32} className={styles.emptyIconSvg} />
                <p className={styles.emptyMessage}>No active promotions</p>
                <p className={styles.emptySubtext}>Create campaigns to engage customers</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
